import type { ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Typography,
} from "@mui/material";

const SEVERIDADE_EVENTO_LABEL: Record<number, string> = {
  0: "Info",
  1: "Atenção",
  2: "Crítico",
};

const SEGMENTO_CARTEIRA_LABEL: Record<number, string> = {
  0: "Saudável",
  1: "Atenção",
  2: "Crítico",
};

/** `TendenciaMonitoramento` */
export const TENDENCIA_MONITORAMENTO_LABEL: Record<number, string> = {
  0: "Estável",
  1: "Piorou",
  2: "Melhorou",
  3: "Sem histórico anterior",
};

function unknownToReadableString(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object" && val !== null && "nome" in val) {
    const n = (val as { nome?: unknown }).nome;
    return typeof n === "string" ? n : String(n ?? "");
  }
  return String(val);
}

/** `SeveridadeEventoMonitoramento` (0–2) ou texto legado. */
export function severidadeComoTexto(val: unknown): string {
  if (typeof val === "number" && Number.isFinite(val)) {
    const r = Math.round(val);
    if (Object.prototype.hasOwnProperty.call(SEVERIDADE_EVENTO_LABEL, r)) {
      return SEVERIDADE_EVENTO_LABEL[r];
    }
  }
  return unknownToReadableString(val);
}

/** `SegmentoCarteiraMonitoramento` (0–2) ou nome legado. */
export function segmentoCarteiraComoTexto(val: unknown): string {
  if (typeof val === "number" && Number.isFinite(val)) {
    const r = Math.round(val);
    if (Object.prototype.hasOwnProperty.call(SEGMENTO_CARTEIRA_LABEL, r)) {
      return SEGMENTO_CARTEIRA_LABEL[r];
    }
  }
  return unknownToReadableString(val);
}

/** Cor da borda lateral em listas de eventos (MUI `theme` path). */
export function corBordaSeveridadeMui(sev: unknown): string {
  if (sev === 2) return "error.main";
  if (sev === 1) return "warning.main";
  if (sev === 0) return "info.main";
  const s = severidadeComoTexto(sev).toLowerCase();
  if (s.includes("crit")) return "error.main";
  if (s.includes("aten")) return "warning.main";
  return "divider";
}

export function tendenciaMonitoramentoComoTexto(val: unknown): string {
  if (typeof val === "number" && Number.isFinite(val)) {
    const r = Math.round(val);
    if (Object.prototype.hasOwnProperty.call(TENDENCIA_MONITORAMENTO_LABEL, r)) {
      return TENDENCIA_MONITORAMENTO_LABEL[r];
    }
  }
  return unknownToReadableString(val);
}

const tendenciaChipProps = (
  val: unknown
): { label: string; color: "success" | "warning" | "error" | "info" | "default" } => {
  if (typeof val === "number" && Number.isFinite(val)) {
    const r = Math.round(val) as 0 | 1 | 2 | 3;
    if (r === 1) return { label: TENDENCIA_MONITORAMENTO_LABEL[1], color: "error" };
    if (r === 2) return { label: TENDENCIA_MONITORAMENTO_LABEL[2], color: "success" };
    if (r === 3) return { label: TENDENCIA_MONITORAMENTO_LABEL[3], color: "info" };
    if (r === 0) return { label: TENDENCIA_MONITORAMENTO_LABEL[0], color: "default" };
  }
  const texto = tendenciaMonitoramentoComoTexto(val);
  const s = texto.toLowerCase();
  if (s.includes("pior")) return { label: texto, color: "error" };
  if (s.includes("melhor")) return { label: texto, color: "success" };
  if (s.includes("hist")) return { label: texto, color: "info" };
  if (s.includes("estável") || s.includes("estavel")) return { label: texto, color: "default" };
  return { label: texto || "—", color: "default" };
};

export function TendenciaChip({ tendencia }: { tendencia?: unknown }) {
  const { label, color } = tendenciaChipProps(tendencia);
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
    />
  );
}

export function formatBRL(n?: number | null): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatDataHora(iso?: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const segmentoChipProps = (
  seg?: unknown
): { label: string; color: "success" | "warning" | "error" | "default" } => {
  if (typeof seg === "number" && Number.isFinite(seg)) {
    const r = Math.round(seg) as 0 | 1 | 2;
    if (r === 0) return { label: SEGMENTO_CARTEIRA_LABEL[0], color: "success" };
    if (r === 1) return { label: SEGMENTO_CARTEIRA_LABEL[1], color: "warning" };
    if (r === 2) return { label: SEGMENTO_CARTEIRA_LABEL[2], color: "error" };
  }
  const texto = segmentoCarteiraComoTexto(seg);
  const s = texto.toLowerCase();
  if (s.includes("saud")) return { label: texto || "Saudável", color: "success" };
  if (s.includes("tenc") || s.includes("aten"))
    return { label: texto || "Atenção", color: "warning" };
  if (s.includes("crit")) return { label: texto || "Crítico", color: "error" };
  return { label: texto || "—", color: "default" };
};

export function SegmentoChip({ segmento }: { segmento?: unknown }) {
  const { label, color } = segmentoChipProps(segmento);
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
    />
  );
}

export function KpiCard({
  titulo,
  valor,
  loading,
}: {
  titulo: string;
  valor: ReactNode;
  loading?: boolean;
}) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography color="text.secondary" variant="body2" gutterBottom>
          {titulo}
        </Typography>
        {loading ? (
          <Skeleton width="60%" height={40} />
        ) : (
          <Typography variant="h5" component="div" fontWeight={600}>
            {valor}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function ApiErrorPanel({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Tentar novamente
        </Button>
      }
    >
      {message}
    </Alert>
  );
}

export function PainelRodape({
  versaoRegras,
  labelDadosAte,
}: {
  versaoRegras?: string | number;
  labelDadosAte?: string;
}) {
  if (versaoRegras == null && !labelDadosAte) return null;
  return (
    <Box
      component="footer"
      sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}
    >
      {labelDadosAte ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {labelDadosAte}
        </Typography>
      ) : null}
      {versaoRegras != null ? (
        <Typography variant="caption" color="text.secondary" display="block">
          Regras: {String(versaoRegras)}
        </Typography>
      ) : null}
    </Box>
  );
}
